import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate } from "@/components/common";
import { EmptyState } from "@/components/telecaller/LeadCard";
import { Bell, Flame, Sparkles, RefreshCw, Info, CheckCheck, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";

const ICONS = { NEW_LEAD: Sparkles, HOT_AI_LEAD: Flame, FOLLOWUP_DUE: Bell, FOLLOWUP_MISSED: Bell, LEAD_REASSIGNED: RefreshCw, AI_OUTCOME: Flame, SYSTEM_ALERT: Info };

export default function Notifications() {
  const navigate = useNavigate();
  const { refreshUnread } = useOutletContext();
  const [items, setItems] = useState(null);

  const load = () => api.get("/notifications").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const open = async (n) => {
    if (!n.is_read) { try { await api.patch(`/notifications/${n.id}/read`); } catch {} refreshUnread && refreshUnread(); }
    if (n.reference_type === "lead" && n.reference_id) navigate(`/app/leads/${n.reference_id}`);
    else load();
  };
  const markAll = async () => { try { await api.post("/notifications/read-all"); toast.success("All marked read"); } catch {} load(); refreshUnread && refreshUnread(); };

  if (items === null) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="font-heading text-xl font-extrabold flex items-center gap-2"><Bell className="h-5 w-5 text-primary" />Alerts</h1>
        {items.some((n) => !n.is_read) && (
          <button onClick={markAll} data-testid="mark-all-read" className="text-xs text-primary flex items-center gap-1"><CheckCheck className="h-4 w-4" />Mark all read</button>
        )}
      </header>

      {items.length === 0 && <EmptyState icon={Bell} title="No notifications." subtitle="New lead & hot-lead alerts show here." />}

      <div className="space-y-2.5">
        {items.map((n) => {
          const Icon = ICONS[n.type] || Bell;
          const hot = n.type === "HOT_AI_LEAD" || n.type === "AI_OUTCOME";
          return (
            <button key={n.id} onClick={() => open(n)} data-testid={`notification-${n.id}`}
              className={`w-full text-left rounded-2xl border p-4 flex gap-3 transition-colors ${
                n.is_read ? "border-slate-800 bg-slate-900/40" : hot ? "border-red-500/40 bg-red-500/[0.06]" : "border-primary/40 bg-primary/[0.06]"
              }`}>
              <span className={`h-9 w-9 shrink-0 rounded-xl flex items-center justify-center ${hot ? "bg-red-500/15 text-red-400" : "bg-primary/15 text-primary"}`}>
                <Icon className="h-4.5 w-4.5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm truncate">{n.title}</span>
                  {!n.is_read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" />}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{n.message}</p>
                <span className="text-[11px] text-slate-600">{fmtDate(n.created_at)}</span>
              </div>
              {n.reference_type === "lead" && (
                <span className="self-center text-primary"><Phone className="h-4 w-4" /></span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
